import React, { useEffect, useState, useRef } from "react";
import { useEventResult } from "../State/EventResultContext";
import { useNavigate } from "react-router-dom";
import StepIndicator from "../Components/StepIndicator";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { SUPPORTED_LANGUAGES } from "../utils/constants";
import { useErrorRenderer } from "../Hooks/useErrorRenderer";
import CustomSelect from "../Components/CustomSelect";
import AppHeader from "../Components/AppHeader";
import { useLoginResult } from "../State/LoginResultContext";
import { useStreaming } from "../State/StreamingContext";

function EventDetail() {
  const [mediaRecorder, setMediaRecorder] = useState();
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const { eventResults } = useEventResult();
  const { loginResults } = useLoginResult();
  const supportedLangCodes = Object.keys(SUPPORTED_LANGUAGES)
  const [speechLanguage, setSpeechLanguage] = useState("en");
  const [message, setMessage, isError, setIsError, resetErrorState] =
    useErrorRenderer();
  const navigate = useNavigate();
  const { start } = useStreaming();

  useEffect(() => {
    let cancelled = false;
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (cancelled) {
          stream.getTracks()?.forEach((t) => t.stop());
          return;
        }
        mediaStreamRef.current = stream;

        const preferredMime = "audio/webm";
        const recorder = MediaRecorder.isTypeSupported(preferredMime)
          ? new MediaRecorder(stream, { mimeType: preferredMime })
          : new MediaRecorder(stream);

        recorder.start(250);
        mediaRecorderRef.current = recorder;
        setMediaRecorder(recorder);
      } catch (error) {
        setMessage(error?.message || "Microphone access denied");
        setIsError(true);
        resetErrorState();
      }
    };
    startRecording();

    return () => {
      cancelled = true;
      try {
        mediaRecorderRef.current?.stop?.();
      } catch {
        // ignore
      }
      try {
        mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
      } catch {
        // ignore
      }
    };
  // Intentionally mount/unmount only: `useErrorRenderer` returns unstable fns,
  // and re-running this effect would repeatedly stop/start the mic.
  }, []);

  return (
    <div className="min-h-full flex-col">
      <AppHeader className="sm:hidden" isSuperUser={loginResults?.isSuperUser} loginResults={loginResults}/>
      <div className="flex flex-1 flex-col sm:justify-center px-8 py-2 sm:py-12 sm:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm flex flex-col items-center">
          <img
            src="logo192.png"
            alt="Seymour Logo"
            className="mx-auto h-11 sm:h-20 w-auto rounded-full hidden sm:block"
          />
          <StepIndicator currentStep={2} className="sm:mt-6" />
          <h2 className="text-center text-[32px] sm:text-[40px] font-extrabold text-white atkinson-font mt-4 tracking-tight">
            Check Live Audio
          </h2>

          <div className="mt-2 text-center">
            <p className="font-bold text-white atkinson-font">
              {eventResults.name}
            </p>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="mb-6 flex flex-col items-center">
            <div className="bg-[#000000] rounded-xl border border-[#FFFFFF20] py-4 px-8 flex items-center justify-center w-full max-w-[400px] shadow-lg shadow-purple-900/20">
              {mediaRecorder ? (
                <LiveAudioVisualizer
                  mediaRecorder={mediaRecorder}
                  width={220}
                  height={50}
                  barColor="#7A2FD3"
                  gap={4}
                  barWidth={6}
                />
              ) : (
                <p className="text-gray-500 text-xs text-center py-4">
                  Waiting for microphone...
                </p>
              )}
            </div>
            <h3 className="font-bold text-white mt-1 atkinson-font tracking-tight">
              Input Monitor
            </h3>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <CustomSelect
                value={speechLanguage}
                options={supportedLangCodes.map((code) => ({
                  value: code,
                  label: SUPPORTED_LANGUAGES[code],
                }))}
                onChange={(val) => setSpeechLanguage(val)}
              />
            </div>

            <button
              type="button"
              onClick={async () => {
                try {
                  await start({
                    eventResults,
                    loginResults,
                    speechLanguage
                  });
                  navigate("/captions-preview");
                } catch (e) {
                  setMessage(e?.message || "Failed to start");
                  setIsError(true);
                  resetErrorState();
                }
              }}
              style={{ border: "2px solid #6A6680" }}
              className="flex w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3] border-2 border-[#6A6680]"
            >
              Start Speech to Text
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetail;